// src/pages/reports/IrAnalysis/ReportIr.tsx
import React, { useEffect, useRef, useState } from "react";
import { Search } from "@mui/icons-material";
import CalendorIcon from "../../../../src/assests/images/calendar-2.svg";
import arrowLeft from "../../../../src/assests/images/arrow-left.svg";
import arrowRight from "../../../../src/assests/images/arrow-right.png";
import doubleArrowLeft from "../../../../src/assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../../src/assests/images/double-arrow-right.svg";
import {
  Box,
  Link,
  Typography,
  TextField,
  InputAdornment,
  Button,
  ButtonGroup,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { debounce } from "lodash";
import ReportsIrTable from "./ReportsIrTable";
import ReportsIrTrTable from "./ReportsIrTrTable";
import ReportIrFilter from "./ReportIrFilter";
import ReportIrTrFilter from "./ReportIrTrFilter";

/** ---------- Types ---------- */
type SummaryFilters = {
  city?: string | string[] | null;
  locations?: string[] | string | null;
  outlet_category?: string[] | null;
  pmi_entity_code?: string[] | null;
  outlet_name?: string[] | null;
};

type TxnFilters = {
  locations?: string[] | string | null;
  outlet_category?: string[] | null;
  pmi_entity_code?: string[] | null;
  outlet_name?: string[] | null;
  transaction_from?: string | null; // "YYYY-MM-DD"
  transaction_to?: string | null;   // "YYYY-MM-DD"
};
const parseYMD = (s?: string | null) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1); // local date, no TZ shift
};

/** normalize helpers (strip only null → undefined; keep strings/arrays) */
const normalizeSummary = (f: SummaryFilters | null) =>
  !f
    ? null
    : ({
        city: f.city ?? undefined,
        locations: f.locations ?? undefined,
        outlet_category: f.outlet_category ?? undefined,
        pmi_entity_code: f.pmi_entity_code ?? undefined,
        outlet_name: f.outlet_name ?? undefined,
      } as {
        city?: string | string[];
        locations?: string | string[];
        outlet_category?: string[];
        pmi_entity_code?: string[];
        outlet_name?: string[];
      });

const normalizeTxn = (f: TxnFilters | null) =>
  !f
    ? null
    : ({
        locations: f.locations ?? undefined,
        outlet_category: f.outlet_category ?? undefined,
        pmi_entity_code: f.pmi_entity_code ?? undefined,
        outlet_name: f.outlet_name ?? undefined,
        transaction_from: f.transaction_from ?? null,
        transaction_to: f.transaction_to ?? null,
      } as {
        locations?: string | string[];
        outlet_category?: string[];
        pmi_entity_code?: string[];
        outlet_name?: string[];
        transaction_from?: string | null;
        transaction_to?: string | null;
      });

/** calendar input */
const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, onClear }, ref) => (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #ccc",
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "0 10px",
        height: "48px",
        width: "301px",
        cursor: "pointer",
      }}
    >
      <input
        ref={ref}
        value={value || "Select Date Range"}
        style={{
          fontSize: "14px",
          border: "none",
          outline: "none",
          flex: 1,
          color: "#000",
          backgroundColor: "transparent",
        }}
        readOnly
        onClick={onClick}
      />
      {value && (
        <img
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          src={
            "data:image/svg+xml;utf8," +
            encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
            )
          }
          alt="Clear"
          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 8 }}
        />
      )}
      <img
        src={CalendorIcon}
        alt="Calendar Icon"
        style={{ height: "20px", width: "20px", cursor: "pointer" }}
        onClick={onClick}
      />
    </div>
  )
);

function ReportIr() {
  /** --- UI State --- */
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]); // UI-only
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSwitch, setIsSwitch] = useState(false); // false=Summary, true=Transactions
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [shouldFetchFilters, setShouldFetchFilters] = useState(false);

  const [startDate, endDate] = dateRange;

  /** --- Filters (separate per tab) --- */
  const [summaryFilters, setSummaryFilters] = useState<SummaryFilters | null>(null);
  const [txnFilters, setTxnFilters] = useState<TxnFilters | null>(null);

  /** debounce search */
  const debouncerRef = useRef<any>(null);
  useEffect(() => {
    debouncerRef.current = debounce((q: string) => setDebouncedSearch(q.trim()), 300);
    return () => debouncerRef.current?.cancel();
  }, []);
  useEffect(() => {
    debouncerRef.current?.(searchQuery);
  }, [searchQuery]);

  /** open correct filter */
  const handleOpenFilter = () => {
    setShouldFetchFilters(true);
    setIsFilterOpen(true);
  };

  const SEARCH_PLACEHOLDERS = {
    summary: "Search by Outlet Name, City, PMI Entity Code, Region",
    transactions: "Search by  Outlet Name, City, PMI Entity Code, Region",
  };
  const searchPlaceholder = isSwitch
    ? SEARCH_PLACEHOLDERS.transactions
    : SEARCH_PLACEHOLDERS.summary;

  /* ---------------------- NEW: sync header dates → txnFilters ---------------------- */
  const toLocalYMD = (d: Date | null) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    // Only the Transactions tab cares about tx date – mirror Master behavior
    const fromStr = toLocalYMD(startDate);
    const toStr = toLocalYMD(endDate);

    setTxnFilters((prev) => {
      const base = prev ?? {};
      return {
        ...base,
        transaction_from: fromStr,
        transaction_to: toStr,
      };
    });
  }, [startDate, endDate]);
  /* ------------------------------------------------------------------------------- */

  return (
    <Box sx={{ background: "#fff", paddingTop: "30px" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 32px",
        }}
      >
        <Box>
          <Link
            href="#"
            sx={{
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: "500",
              color: "#696A6B",
              paddingRight: "10px",
            }}
          >
            IR Analysis Report
          </Link>
          <Typography
            variant="h3"
            sx={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#000",
              marginTop: "16px",
            }}
          >
            IR Analysis Report
          </Typography>
        </Box>

        {/* Right controls: Filter + Switch */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={handleOpenFilter}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              bgcolor: "#C8172F",
              color: "#fff",
              borderColor: "#C8172F",
              borderRadius: "8px",
              height: "40px",
              width: "74px",
              "&:hover": {
                bgcolor: "#a31425",
                borderColor: "#a31425",
              },
            }}
          >
            Filter
          </Button>

          <ButtonGroup variant="outlined" sx={{ mr: 0 }}>
            <Button
              onClick={() => setIsSwitch(false)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: !isSwitch ? "#C8172F" : "transparent",
                color: !isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "40px",
                width: "110px",
                "&:hover": {
                  bgcolor: !isSwitch ? "#a31425" : "rgba(200,23,47,0.1)",
                  borderColor: "#a31425",
                },
              }}
            >
              Summary
            </Button>

            <Button
              onClick={() => setIsSwitch(true)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: isSwitch ? "#C8172F" : "transparent",
                color: isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "40px",
                width: "110px",
                "&:hover": {
                  bgcolor: isSwitch ? "#a31425" : "rgba(200,23,47,0.1)",
                  borderColor: "#a31425",
                },
              }}
            >
              Transactions
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* Search + Date row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "16px",
          padding: "0 32px 30px",
        }}
      >
        {/* Search */}
        <Box sx={{ maxWidth: "520px", flexGrow: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            type="search"
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ height: "20px", width: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": { border: "2px solid #F5F5F5" },
                height: "48px",
              },
            }}
          />
        </Box>

        {/* Date Picker (header drives txnFilters now) */}
        <Box sx={{ display: "flex" }}>
          <Box>
            <DatePicker
              selectsRange
              startDate={startDate || undefined}
              endDate={endDate || undefined}
              onChange={(update: [Date | null, Date | null] | null) =>
                setDateRange(update ?? [null, null])
              }
              dateFormat="yyyy-MM-dd"
              customInput={
                <CustomInput
                  onClear={() => {
                    // clear header date
                    setDateRange([null, null]);
                    // also clear txn date fields (mirrors Master)
                    setTxnFilters((prev) =>
                      prev
                        ? { ...prev, transaction_from: null, transaction_to: null }
                        : prev
                    );
                  }}
                />
              }
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                decreaseYear,
                increaseYear,
              }) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <button onClick={decreaseYear} aria-label="Previous year">
                    <img src={doubleArrowLeft} alt="Previous year" />
                  </button>
                  <button onClick={decreaseMonth} aria-label="Previous month">
                    <img src={arrowLeft} alt="Previous month" />
                  </button>

                  <span style={{ margin: "0 10px" }}>
                    {date.toLocaleString("default", {
                      year: "numeric",
                      month: "long",
                    })}
                  </span>

                  <button onClick={increaseMonth} aria-label="Next month">
                    <img src={arrowRight} alt="Next month" />
                  </button>
                  <button onClick={increaseYear} aria-label="Next year">
                    <img src={doubleArrowRight} alt="Next year" />
                  </button>
                </div>
              )}
            />
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          width: "100%",
          background: "#f9fbfc",
          borderTop: "1px solid #d3d3d3",
        }}
      >
        {!isSwitch ? (
          <ReportsIrTable
            searchQuery={debouncedSearch}
            appliedFilters={normalizeSummary(summaryFilters)}
            dateRange={[null, null]} // UI-only here
          />
        ) : (
          <ReportsIrTrTable
            searchQuery={debouncedSearch}
            // @ts-ignore — allow the table to consume richer txn filters including dates
            appliedFilters={normalizeTxn(txnFilters)}
            onBackendFilterOptions={() => {}}
          />
        )}
      </Box>

      {/* Filter Modal (renders the correct one per tab) */}
      {isFilterOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            padding: 16,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsFilterOpen(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "center", width: "auto" }}>
            {!isSwitch ? (
              <ReportIrFilter
                isOpen={isFilterOpen}
                shouldFetch={shouldFetchFilters}
                onFetched={() => setShouldFetchFilters(false)}
                initialFilters={{
                  locations: (summaryFilters?.locations as string[] | null) ?? null,
                  outlet_category: summaryFilters?.outlet_category ?? null,
                  pmi_entity_code: summaryFilters?.pmi_entity_code ?? null,
                  outlet_name: summaryFilters?.outlet_name ?? null,
                }}
                onApplyFilters={(payload) => {
                  setSummaryFilters({
                    locations: payload.locations ?? null,
                    outlet_category: payload.outlet_category ?? null,
                    pmi_entity_code: payload.pmi_entity_code ?? null,
                    outlet_name: payload.outlet_name ?? null,
                  });
                  setIsFilterOpen(false);
                }}
                onCancel={() => setIsFilterOpen(false)}
              />
            ) : (
              <ReportIrTrFilter
  isOpen={isFilterOpen}
  shouldFetch={shouldFetchFilters}
  onFetched={() => setShouldFetchFilters(false)}
  initialFilters={{
    locations: (txnFilters?.locations as string[] | null) ?? null,
    outlet_category: txnFilters?.outlet_category ?? null,
    pmi_entity_code: txnFilters?.pmi_entity_code ?? null,
    outlet_name: txnFilters?.outlet_name ?? null,
    transaction_from: txnFilters?.transaction_from ?? null,
    transaction_to: txnFilters?.transaction_to ?? null,
  }}
  onApplyFilters={(payload) => {
    // keep existing behavior
    setTxnFilters({
      locations: payload.locations ?? null,
      outlet_category: payload.outlet_category ?? null,
      pmi_entity_code: payload.pmi_entity_code ?? null,
      outlet_name: payload.outlet_name ?? null,
      transaction_from: payload.transaction_from ?? null,
      transaction_to: payload.transaction_to ?? null,
    });

    // NEW: mirror popup dates into the header DatePicker
    setDateRange([parseYMD(payload.transaction_from), parseYMD(payload.transaction_to)]);

    setIsFilterOpen(false);
  }}
  onCancel={() => setIsFilterOpen(false)}
/>

            )}
          </div>
        </div>
      )}
    </Box>
  );
}

export default ReportIr;
